import { UAContext } from '@quentin-sommer/react-useragent';
import { Form, Formik } from 'formik';
import _ from 'lodash';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { EmergencyForm } from '~components/base-form';
import Button from '~components/button';
import Typography from '~components/typography';
import { RelationshipOptions } from '~graphql/__generated__/globalTypes';
import { useTranslation } from '~i18n';
import { RootState } from '~store';
import deduplicatePhoneCodes from '~utils/get-phone-codes';

export interface IEmergencyProps {
  id: string | null;
  contactEmail: string | null;
  firstName: any | null;
  middleName: string | null;
  lastName: any | null;
  title: string | null;
  dateOfBirth: any | null;
  mobilePhoneAreaCode: string | null;
  mobilePhoneNumber: string | null;
  homePhoneAreaCode: string | null;
  homePhoneNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postcode: string | null;
  countryTerritory: string | null;
  countryTerritoryId: string | null;
  cityTown: string | null;
  cityTownId: string | null;
  relationship: RelationshipOptions | null;
  contactWithoutPermission: boolean | null;
}

interface IEmergencyContainerProps {
  emergency: IEmergencyProps | null | undefined;
  onSuccess?: (values: any) => void;
  onCancel?: () => void;
  submitText?: string;
  cancelText?: string;
  loading?: boolean;
  isEdit?: boolean;
}

const EmergencyContainer: React.FC<IEmergencyContainerProps> = ({
  emergency,
  onSuccess,
  onCancel,
  submitText,
  cancelText,
  loading,
  isEdit,
}) => {
  const { t } = useTranslation(['student_portal']);
  const uaResults = useContext(UAContext).uaResults as UAResults;
  const isMobile = uaResults.mobile;
  const { viewer } = useSelector((state: RootState) => state);
  const emailValidError = t('form.validation.format_error', {
    field_name: t('form.field.email_address').toLowerCase(),
  });
  const notEqualToTenantEmailError = t('form.validation.not_equal_to_emergency_email');
  const phoneValidError = t('form.validation.format_error', {
    field_name: t('form.field.phone').toLowerCase(),
  });
  const EmergencyFormSchema = Yup.object().shape({
    contactEmail: Yup.string()
      .trim()
      .email(emailValidError)
      .test('match', notEqualToTenantEmailError, email => email !== viewer?.email)
      .required(),
    dateOfBirth: Yup.mixed().required(),
    title: Yup.mixed().required(),
    firstName: Yup.string()
      .trim()
      .required(),
    middleName: Yup.string()
      .trim()
      .strict(true),
    lastName: Yup.string()
      .trim()
      .required(),
    mobilePhoneAreaCode: Yup.mixed().required(),
    mobilePhoneNumber: Yup.number()
      .required()
      .typeError(phoneValidError),
    homePhoneNumber: Yup.number().typeError(phoneValidError),
    addressLine1: Yup.string()
      .trim()
      .required(),
    addressLine2: Yup.string()
      .trim()
      .strict(true),
    countryTerritoryId: Yup.mixed().required(),
    cityTownId: Yup.mixed().required(),
    postcode: Yup.string()
      .trim()
      .strict(true),
    relationship: Yup.mixed().required(),
    contactWithoutPermission: Yup.boolean(),
  });

  const initialTitle = emergency?.title
    ? {
        label: `${_.startCase(_.toLower(emergency?.title))}.`,
        value: emergency?.title,
      }
    : '';
  const initiaMobilePhoneAreaCode = emergency?.mobilePhoneAreaCode
    ? {
        value: emergency?.mobilePhoneAreaCode,
        label: emergency?.mobilePhoneAreaCode,
      }
    : deduplicatePhoneCodes[0];

  const initiaHomePhoneAreaCode = emergency?.homePhoneAreaCode
    ? {
        value: emergency?.homePhoneAreaCode,
        label: emergency?.homePhoneAreaCode,
      }
    : deduplicatePhoneCodes[0];
  const initialCountryTerritoryId = emergency?.countryTerritoryId
    ? {
        value: emergency?.countryTerritoryId,
        label: emergency?.countryTerritory,
      }
    : '';
  const initialCityTownId = emergency?.cityTown
    ? {
        value: emergency?.cityTownId,
        label: emergency?.cityTown,
      }
    : '';
  const initialRelationShip = emergency?.relationship
    ? {
        label: t(`form.options.relation.${emergency?.relationship.toLowerCase()}`),
        value: emergency?.relationship,
      }
    : '';
  const initialValues = {
    contactEmail: emergency?.contactEmail || '',
    title: initialTitle || '',
    firstName: emergency?.firstName || '',
    middleName: emergency?.middleName || '',
    lastName: emergency?.lastName || '',
    mobilePhoneAreaCode: initiaMobilePhoneAreaCode,
    mobilePhoneNumber: emergency?.mobilePhoneNumber || '',
    homePhoneAreaCode: initiaHomePhoneAreaCode,
    homePhoneNumber: emergency?.homePhoneNumber || '',
    dateOfBirth: emergency?.dateOfBirth || '',
    addressLine1: emergency?.addressLine1 || '',
    addressLine2: emergency?.addressLine2 || '',
    countryTerritoryId: initialCountryTerritoryId,
    cityTownId: initialCityTownId,
    postcode: emergency?.postcode || '',
    relationship: initialRelationShip,
    contactWithoutPermission: isEdit ? emergency?.contactWithoutPermission : true,
  };
  const onSubmit = (values: any) => {
    const emergencyValues = JSON.parse(JSON.stringify(values));
    Object.keys(emergencyValues).forEach(key => {
      if (emergencyValues[key].hasOwnProperty('label')) {
        if (key === 'cityTownId') {
          emergencyValues['cityTown'] = emergencyValues[key].label;
          emergencyValues[key] = !!emergencyValues[key]?.__isNew__
            ? null
            : emergencyValues[key].value || null;
        } else {
          emergencyValues[key] = emergencyValues[key].value || null;
        }
      } else {
        if (emergencyValues[key] === '') {
          emergencyValues[key] = null;
        }
      }
    });
    if (!emergencyValues['homePhoneNumber']) {
      emergencyValues['homePhoneNumber'] = null;
      emergencyValues['homePhoneAreaCode'] = null;
    }
    if (isEdit) {
      emergencyValues['id'] = emergency?.id;
    }
    if (onSuccess) {
      onSuccess(emergencyValues);
    }
  };
  return (
    <>
      <Formik
        initialValues={{ ...initialValues }}
        onSubmit={onSubmit}
        validateOnBlur
        validationSchema={EmergencyFormSchema}
      >
        {props => (
          <Form>
            <EmergencyForm
              setFieldValue={props.setFieldValue}
              deduplicatePhoneCodes={deduplicatePhoneCodes}
              values={props.values}
            />
            <div className="emergency-form__btns">
              <Button
                size="large"
                loading={loading}
                fullWidth={isMobile}
                type="submit"
                className="emergency-form__submit-btn"
              >
                {submitText || t('check_in.modal.save')}
              </Button>
              {cancelText && onCancel && (
                <Typography
                  variant="body2"
                  textDecoration="underline"
                  cursor="pointer"
                  onClick={() => {
                    onCancel();
                  }}
                >
                  {cancelText}
                </Typography>
              )}
            </div>
          </Form>
        )}
      </Formik>
      <style jsx>{`
        .emergency-form__btns {
          margin-top: 20px;
        }
        :global(.emergency-form__submit-btn) {
          margin-right: 36px;
        }
        @media screen and (max-width: 768px) {
          .emergency-form__btns {
            text-align: center;
          }
          :global(.emergency-form__submit-btn) {
            margin-right: 0;
            margin-bottom: 16px;
          }
        }
      `}</style>
    </>
  );
};

export default EmergencyContainer;
